
/**
 * Creates a debounced version of a function
 * @param fn The function to debounce
 * @param ms Delay in milliseconds (default 300ms)
 * @returns A debounced version of the function
 */
export const debounce = <F extends (...args: any[]) => any>(fn: F, ms = 300): (...args: Parameters<F>) => void => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<F>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, ms);
  };
};

/**
 * Creates a debounced version of a function that returns a promise
 * @param fn The async function to debounce
 * @param ms Delay in milliseconds (default 300ms)
 * @returns A debounced version of the async function
 */
export const debouncedAsync = <F extends (...args: any[]) => Promise<any>>(fn: F, ms = 300): (...args: Parameters<F>) => Promise<ReturnType<F> | undefined> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let currentPromise: Promise<ReturnType<F>> | null = null;

  return (...args: Parameters<F>) => {
    // Cancel any existing timeout
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    // Create a new promise that resolves after the debounce delay
    return new Promise<ReturnType<F> | undefined>((resolve) => {
      timeoutId = setTimeout(async () => {
        try {
          // Execute the function and store the promise
          currentPromise = fn(...args);
          const result = await currentPromise;
          resolve(result);
        } catch (error) {
          resolve(undefined);
        } finally {
          timeoutId = null;
          currentPromise = null;
        }
      }, ms);
    });
  };
};
